import {
	GTM_CONVERSION_EVENTS,
	type GtmConversionDataMap,
	type GtmConversionName,
} from "@repo/analytics";
import posthog from "posthog-js";
import "./provider/linkedin/insight";

/**
 * The LinkedIn conversion id for each go-to-market conversion. Each env var is
 * referenced by its full literal name so Next inlines it into the client
 * bundle; a dynamic `process.env[name]` lookup would always be undefined there.
 */
export function linkedInConversionId(name: GtmConversionName): number | null {
	const raw = {
		[GTM_CONVERSION_EVENTS.GTM_REPORT_PREVIEW_RUN]:
			process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_REPORT_PREVIEW,
		[GTM_CONVERSION_EVENTS.GTM_REPORT_PURCHASED]:
			process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_REPORT_PURCHASE,
		[GTM_CONVERSION_EVENTS.GTM_SIGNUP_COMPLETED]:
			process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_SIGNUP,
		[GTM_CONVERSION_EVENTS.GTM_DEMO_BOOKED]:
			process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_DEMO_BOOKED,
	}[name]?.trim();

	if (!raw) {
		return null;
	}

	const id = Number(raw);
	return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * Sends one go-to-market conversion to PostHog and to the LinkedIn Insight
 * Tag. Callable outside React. A no-op on the server.
 *
 * PostHog is skipped when no PostHog key is configured, LinkedIn when the
 * conversion has no id configured; each is independent of the other. LinkedIn
 * receives the conversion id and nothing else, so no personal data reaches it.
 * When the Insight Tag is not loaded (development, white-label portals)
 * `window.lintrk` is undefined and the LinkedIn call is skipped.
 */
export function trackConversion<N extends GtmConversionName>(
	name: N,
	data: GtmConversionDataMap[N],
): void {
	if (typeof window === "undefined") {
		return;
	}

	if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
		try {
			posthog.capture(name, { ...data });
		} catch {
			// Analytics must never break the page.
		}
	}

	const conversionId = linkedInConversionId(name);
	if (conversionId !== null) {
		try {
			window.lintrk?.("track", { conversion_id: conversionId });
		} catch {
			// Analytics must never break the page.
		}
	}
}

/**
 * `trackConversion`, at most once per `dedupeKey` in this browser, remembered
 * in localStorage so a reload does not fire it again. Returns whether it fired.
 *
 * When storage cannot be read or written (blocked site data) it does not fire:
 * without a record of having fired, every later page load would fire again, and
 * a lost conversion is cheaper than an inflated count.
 */
export function trackConversionOnce<N extends GtmConversionName>(
	name: N,
	data: GtmConversionDataMap[N],
	dedupeKey: string,
): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const storageKey = `spyglasses:conversion:${name}:${dedupeKey}`;
	try {
		if (window.localStorage.getItem(storageKey)) {
			return false;
		}
		window.localStorage.setItem(storageKey, new Date().toISOString());
	} catch {
		return false;
	}

	trackConversion(name, data);
	return true;
}
